import { create } from 'zustand';
import api from '../utils/api';

const getSafeUser = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch (e) {
    localStorage.removeItem('user');
    return null;
  }
};

const useAuthStore = create((set, get) => ({
  user: getSafeUser(),
  accessToken: localStorage.getItem('accessToken') || null,
  refreshToken: localStorage.getItem('refreshToken') || null,
  isAuthenticated: !!localStorage.getItem('accessToken'),
  authChecked: false,
  isChecking: false,

  setAuth: (user, accessToken, refreshToken) => {
    if (user) localStorage.setItem('user', JSON.stringify(user));
    if (accessToken) localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    set({
      user,
      accessToken: accessToken || get().accessToken,
      refreshToken: refreshToken || get().refreshToken,
      isAuthenticated: true,
      authChecked: true
    });
  },

  setTokens: (accessToken, refreshToken) => {
    if (accessToken) localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    set({ accessToken, refreshToken });
  },

  logout: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      authChecked: true
    });
  },

  isAdmin: () => {
    const state = get();
    return state.user?.role === 'ADMIN';
  },

  // Validate token and refresh user on app start
  checkAuth: async () => {
    const { isChecking, accessToken } = get();
    if (isChecking || !accessToken) {
      set({ authChecked: true });
      return;
    }

    set({ isChecking: true });
    try {
      const res = await api.get('/users/profile');
      const user = res.data?.data?.user || res.data?.data || res.data;

      if (!user || typeof user !== 'object' || !user.id) {
        throw new Error('Invalid user profile data');
      }

      set({ user, isAuthenticated: true, authChecked: true, isChecking: false });
      localStorage.setItem('user', JSON.stringify(user));
    } catch (e) {
      console.error('Auth verification failed:', e);

      // Critical: Only clear session if it's a definitive auth failure (401/403)
      // If it's a network error (no response), keep the local session tokens
      if (e.response && (e.response.status === 401 || e.response.status === 403)) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
      }

      set({ authChecked: true, isChecking: false });
    }
  }
}));

export { useAuthStore };
